// src/pages/Accounting/Reconcile/Session/FilterPopover.tsx

import type React from "react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Calendar as CalendarIcon, X } from "lucide-react";
import { Calendar, formatIsoToUserDate, parseUserDate } from "../../../../components/Calendar";
import { PALETTE } from "../constants";
import type { Account } from "../types";
import type { SessionFilterState, TransactionKind } from "./sessionTypes";
import { TRANSACTION_KIND_LABELS } from "./sessionTypes";

interface FilterPopoverProps {
  state: SessionFilterState;
  onChange: (state: SessionFilterState) => void;
  onApply: () => void;
  onReset: () => void;
  onClose: () => void;
  account: Account | undefined;
  payees: string[];
  anchorRef: React.RefObject<HTMLElement | null>;
}

const POP_WIDTH = 620;

export function FilterPopover({
  state,
  onChange,
  onApply,
  onReset,
  onClose,
  account: _account,
  payees,
  anchorRef,
}: FilterPopoverProps) {
  const [fromCalOpen, setFromCalOpen] = useState(false);
  const [toCalOpen, setToCalOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const fromRef = useRef<HTMLDivElement>(null);
  const toRef = useRef<HTMLDivElement>(null);
  const popRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const compute = () => {
      const anchor = anchorRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      let left = rect.left;
      if (left + POP_WIDTH > window.innerWidth - 16) {
        left = Math.max(16, window.innerWidth - POP_WIDTH - 16);
      }
      setCoords({ top: rect.bottom + 6, left });
    };
    compute();
    window.addEventListener("resize", compute);
    window.addEventListener("scroll", compute, true);
    return () => {
      window.removeEventListener("resize", compute);
      window.removeEventListener("scroll", compute, true);
    };
  }, [anchorRef]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (popRef.current?.contains(target)) return;
      if (anchorRef.current?.contains(target)) return;
      onClose();
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [onClose, anchorRef]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (fromRef.current && !fromRef.current.contains(e.target as Node)) setFromCalOpen(false);
      if (toRef.current && !toRef.current.contains(e.target as Node)) setToCalOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const update = <K extends keyof SessionFilterState>(key: K, value: SessionFilterState[K]) => {
    onChange({ ...state, [key]: value });
  };

  const fromIso = parseUserDate(state.fromDate);
  const toIso = parseUserDate(state.toDate);

  if (!coords) return null;

  const popStyle: React.CSSProperties = {
    position: "fixed",
    top: coords.top,
    left: coords.left,
    background: "#fff",
    border: `1px solid ${PALETTE.line}`,
    borderRadius: 12,
    boxShadow: "0 10px 32px rgba(0,0,0,0.14)",
    padding: "20px 20px 16px",
    width: POP_WIDTH,
    zIndex: 1000,
  };

  return createPortal(
    <div ref={popRef} style={popStyle}>
      <div style={headStyle}>
        <div style={titleStyle}>Filter</div>
        <button type="button" onClick={onClose} style={closeBtnStyle} aria-label="Close filters">
          <X size={16} />
        </button>
      </div>

      <div style={fieldLabelStyle}>Find</div>
      <input
        type="text"
        value={state.find}
        onChange={(e) => update("find", e.target.value)}
        placeholder="Memo, Ref. no, $amt, >$amt, <$amt"
        style={searchStyle}
      />

      <div style={gridStyle}>
        <div style={fieldStyle}>
          <label style={fieldLabelStyle}>Cleared status</label>
          <select
            value={state.clearedStatus}
            onChange={(e) => update("clearedStatus", e.target.value as SessionFilterState["clearedStatus"])}
            style={selectStyle}
          >
            <option value="all">All</option>
            <option value="cleared">Cleared</option>
            <option value="uncleared">Uncleared</option>
            <option value="needs-review">Needs review</option>
          </select>
        </div>
        <div style={fieldStyle}>
          <label style={fieldLabelStyle}>Transaction type</label>
          <select
            value={state.transactionType}
            onChange={(e) => update("transactionType", e.target.value as SessionFilterState["transactionType"])}
            style={selectStyle}
          >
            <option value="all">All</option>
            {(Object.keys(TRANSACTION_KIND_LABELS) as TransactionKind[]).map((k) => (
              <option key={k} value={k}>{TRANSACTION_KIND_LABELS[k]}</option>
            ))}
          </select>
        </div>
        <div style={fieldStyle}>
          <label style={fieldLabelStyle}>Payee</label>
          <select
            value={state.payee}
            onChange={(e) => update("payee", e.target.value)}
            style={selectStyle}
          >
            <option value="all">All</option>
            {payees.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
      </div>

      <div style={gridStyle}>
        <div style={fieldStyle}>
          <label style={fieldLabelStyle}>Date</label>
          <select
            value={state.dateMode}
            onChange={(e) => update("dateMode", e.target.value as SessionFilterState["dateMode"])}
            style={selectStyle}
          >
            <option value="statement-period">Statement period</option>
            <option value="custom">Custom range</option>
          </select>
        </div>
        <div style={{ ...fieldStyle, position: "relative" }} ref={fromRef}>
          <label style={fieldLabelStyle}>From</label>
          <div style={dateBoxStyle} onClick={() => state.dateMode === "custom" && setFromCalOpen((v) => !v)}>
            <span style={{ color: state.fromDate ? PALETTE.ink : PALETTE.ink4, fontSize: 13 }}>{state.fromDate || "dd/mm/yyyy"}</span>
            <CalendarIcon size={14} color={PALETTE.ink3} />
          </div>
          {fromCalOpen && state.dateMode === "custom" && (
            <Calendar
              valueIso={fromIso ?? undefined}
              onSelect={(iso) => { update("fromDate", formatIsoToUserDate(iso)); setFromCalOpen(false); }}
              onClear={() => { update("fromDate", ""); setFromCalOpen(false); }}
            />
          )}
        </div>
        <div style={{ ...fieldStyle, position: "relative" }} ref={toRef}>
          <label style={fieldLabelStyle}>To</label>
          <div style={dateBoxStyle} onClick={() => state.dateMode === "custom" && setToCalOpen((v) => !v)}>
            <span style={{ color: state.toDate ? PALETTE.ink : PALETTE.ink4, fontSize: 13 }}>{state.toDate || "dd/mm/yyyy"}</span>
            <CalendarIcon size={14} color={PALETTE.ink3} />
          </div>
          {toCalOpen && state.dateMode === "custom" && (
            <Calendar
              valueIso={toIso ?? undefined}
              onSelect={(iso) => { update("toDate", formatIsoToUserDate(iso)); setToCalOpen(false); }}
              onClear={() => { update("toDate", ""); setToCalOpen(false); }}
            />
          )}
        </div>
      </div>

      <div style={footStyle}>
        <button type="button" onClick={onReset} style={resetBtnStyle}>Reset</button>
        <button type="button" onClick={onApply} style={applyBtnStyle}>Apply</button>
      </div>
    </div>,
    document.body,
  );
}

const headStyle: React.CSSProperties = { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 };
const titleStyle: React.CSSProperties = { fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", color: PALETTE.ink3, fontWeight: 700 };
const closeBtnStyle: React.CSSProperties = { background: "transparent", border: 0, color: PALETTE.ink3, cursor: "pointer", padding: 2, display: "flex", alignItems: "center" };
const searchStyle: React.CSSProperties = { width: "100%", padding: "10px 14px", border: `1px solid ${PALETTE.line}`, borderRadius: 8, background: "#fff", fontSize: 13.5, color: PALETTE.ink, fontFamily: "inherit", outline: "none", marginBottom: 14 };
const gridStyle: React.CSSProperties = { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginBottom: 14 };
const fieldStyle: React.CSSProperties = {};
const fieldLabelStyle: React.CSSProperties = { display: "block", fontSize: 11.5, color: PALETTE.ink2, fontWeight: 600, marginBottom: 5 };
const selectStyle: React.CSSProperties = { width: "100%", padding: "8px 12px", border: `1px solid ${PALETTE.line}`, borderRadius: 7, background: "#fff", fontSize: 13, color: PALETTE.ink, fontFamily: "inherit", cursor: "pointer" };
const dateBoxStyle: React.CSSProperties = { width: "100%", padding: "8px 12px", border: `1px solid ${PALETTE.line}`, borderRadius: 7, background: "#fff", display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" };
const footStyle: React.CSSProperties = { display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 14, borderTop: `1px solid ${PALETTE.lineSoft}` };
const resetBtnStyle: React.CSSProperties = { background: "#fff", border: `1px solid ${PALETTE.line}`, color: PALETTE.ink, fontWeight: 600, fontSize: 12.5, padding: "7px 14px", borderRadius: 7, cursor: "pointer", fontFamily: "inherit" };
const applyBtnStyle: React.CSSProperties = { background: "#0E1A1A", color: "#fff", border: 0, fontWeight: 600, fontSize: 12.5, padding: "7px 14px", borderRadius: 7, cursor: "pointer", fontFamily: "inherit" };